// Visitor Tracking Service
// Tracks page visits, session duration, and visitor analytics

import { collection, addDoc, serverTimestamp, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';

// Get or create visitor ID
const getVisitorId = () => {
  let visitorId = localStorage.getItem('visitorId');
  if (!visitorId) {
    visitorId = 'visitor_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('visitorId', visitorId);
    localStorage.setItem('firstVisit', new Date().toISOString());
  }
  return visitorId;
};

// Detect device type
const getDeviceType = () => {
  const width = window.screen.width;
  const userAgent = navigator.userAgent.toLowerCase();
  
  if (/tablet|ipad|playbook|silk/i.test(userAgent) || (width >= 600 && width < 1024)) {
    return 'tablet';
  }
  if (/mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/i.test(userAgent) || width < 600) {
    return 'mobile';
  }
  return 'desktop';
};

// Detect browser
const getBrowser = () => {
  const userAgent = navigator.userAgent;
  if (userAgent.indexOf('Firefox') > -1) return 'Firefox';
  if (userAgent.indexOf('Chrome') > -1 && userAgent.indexOf('Edg') === -1) return 'Chrome';
  if (userAgent.indexOf('Safari') > -1 && userAgent.indexOf('Chrome') === -1) return 'Safari';
  if (userAgent.indexOf('Edg') > -1) return 'Edge';
  if (userAgent.indexOf('Opera') > -1 || userAgent.indexOf('OPR') > -1) return 'Opera';
  return 'Other';
};

// Detect OS
const getOS = () => {
  const userAgent = navigator.userAgent;
  if (userAgent.indexOf('Windows') > -1) return 'Windows';
  if (userAgent.indexOf('Mac') > -1) return 'macOS';
  if (userAgent.indexOf('Linux') > -1) return 'Linux';
  if (userAgent.indexOf('Android') > -1) return 'Android';
  if (userAgent.indexOf('iOS') > -1 || /iPad|iPhone|iPod/.test(userAgent)) return 'iOS';
  return 'Other';
};

// Algerian states (wilayas) mapping
const algerianStates = {
  'Adrar': 'أدرار',
  'Chlef': 'الشلف',
  'Laghouat': 'الأغواط',
  'Oum El Bouaghi': 'أم البواقي',
  'Batna': 'باتنة',
  'Béjaïa': 'بجاية',
  'Biskra': 'بسكرة',
  'Béchar': 'بشار',
  'Blida': 'البليدة',
  'Bouira': 'البويرة',
  'Tamanrasset': 'تمنراست',
  'Tébessa': 'تبسة',
  'Tlemcen': 'تلمسان',
  'Tiaret': 'تيارت',
  'Tizi Ouzou': 'تيزي وزو',
  'Algiers': 'الجزائر',
  'Djelfa': 'الجلفة',
  'Jijel': 'جيجل',
  'Sétif': 'سطيف',
  'Saïda': 'سعيدة',
  'Skikda': 'سكيكدة',
  'Sidi Bel Abbès': 'سيدي بلعباس',
  'Annaba': 'عنابة',
  'Guelma': 'قالمة',
  'Constantine': 'قسنطينة',
  'Médéa': 'المدية',
  'Mostaganem': 'مستغانم',
  'M\'Sila': 'المسيلة',
  'Mascara': 'معسكر',
  'Ouargla': 'ورقلة',
  'Oran': 'وهران',
  'El Bayadh': 'البيض',
  'Illizi': 'إليزي',
  'Bordj Bou Arréridj': 'برج بوعريريج',
  'Boumerdès': 'بومرداس',
  'El Tarf': 'الطارف',
  'Tindouf': 'تندوف',
  'Tissemsilt': 'تيسمسيلت',
  'El Oued': 'الوادي',
  'Khenchela': 'خنشلة',
  'Souk Ahras': 'سوق أهراس',
  'Tipaza': 'تيبازة',
  'Mila': 'ميلة',
  'Aïn Defla': 'عين الدفلى',
  'Naâma': 'النعامة',
  'Aïn Témouchent': 'عين تيموشنت',
  'Ghardaïa': 'غرداية',
  'Relizane': 'غليزان',
  'Timimoun': 'تيميمون',
  'Bordj Badji Mokhtar': 'برج باجي مختار',
  'Ouled Djellal': 'أولاد جلال',
  'Béni Abbès': 'بني عباس',
  'In Salah': 'عين صالح',
  'In Guezzam': 'عين قزام',
  'Touggourt': 'تقرت',
  'Djanet': 'جانت',
  'El M\'Ghair': 'المغير',
  'El Meniaa': 'المنيعة'
};

// Get location from IP (using free API) - Focus on Algerian states
const getLocationFromIP = async () => {
  try {
    // Using ip-api.com which provides better region/state data
    const response = await fetch('https://ip-api.com/json/?fields=status,country,countryCode,city,regionName,timezone', {
      method: 'GET'
    });
    
    if (response.ok) {
      const data = await response.json();
      if (data.status === 'success') {
        // If visitor is from Algeria, get the state (wilaya)
        let state = data.regionName || 'غير محدد';
        let stateArabic = algerianStates[state] || state;
        
        // If we got a city but not a clear state, try to map it
        if (state === 'غير محدد' && data.city) {
          // Some cities might help identify the state
          state = data.city;
          stateArabic = algerianStates[state] || state;
        }
        
        return {
          country: 'Algeria',
          countryCode: 'DZ',
          city: data.city || 'غير محدد',
          state: state,
          stateArabic: stateArabic,
          timezone: data.timezone || 'Unknown'
        };
      }
    }
  } catch (error) {
    console.log('Location API error:', error);
  }
  
  // Fallback: try ipapi.co
  try {
    const response = await fetch('https://ipapi.co/json/', {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      }
    });
    
    if (response.ok) {
      const data = await response.json();
      let state = data.region || 'غير محدد';
      let stateArabic = algerianStates[state] || state;
      
      return {
        country: 'Algeria',
        countryCode: 'DZ',
        city: data.city || 'غير محدد',
        state: state,
        stateArabic: stateArabic,
        timezone: data.timezone || 'Unknown'
      };
    }
  } catch (error) {
    console.log('Alternative location API error:', error);
  }
  
  return {
    country: 'Algeria',
    countryCode: 'DZ',
    city: 'غير محدد',
    state: 'غير محدد',
    stateArabic: 'غير محدد',
    timezone: 'Unknown'
  };
};

// Check if path is an admin page
const isAdminPage = (path) => {
  const adminPaths = [
    '/AdminHomeScreen',
    '/AddGuppyScreen',
    '/AddAquariumScreen',
    '/AddPlantScreen',
    '/AddFoodScreen',
    '/ViewOrdersScreen',
    '/admin/chat',
    '/admin/chatlist',
    '/admin/page',
    '/stats'
  ];
  return adminPaths.some(adminPath => path.startsWith(adminPath));
};

// Track page visit
export const trackPageVisit = async (pagePath, pageTitle) => {
  try {
    // Skip tracking for admin pages
    if (isAdminPage(pagePath)) {
      return;
    }

    const visitorId = getVisitorId();
    const sessionId = sessionStorage.getItem('sessionId') || 'session_' + Date.now();
    
    if (!sessionStorage.getItem('sessionId')) {
      sessionStorage.setItem('sessionId', sessionId);
      sessionStorage.setItem('sessionStart', Date.now().toString());
    }

    // Get device and browser info
    const deviceType = getDeviceType();
    const browser = getBrowser();
    const os = getOS();
    
    // Get location (only for new visitors to avoid too many API calls)
    let location = {
      country: 'Algeria',
      countryCode: 'DZ',
      city: 'غير محدد',
      state: 'غير محدد',
      stateArabic: 'غير محدد',
      timezone: 'Unknown'
    };
    
    // Only fetch location for new visitors or if not stored
    const storedLocation = localStorage.getItem('visitorLocation');
    if (storedLocation) {
      try {
        location = JSON.parse(storedLocation);
        // Ensure all required fields exist
        location = {
          country: location.country || 'Algeria',
          countryCode: location.countryCode || 'DZ',
          city: location.city || 'غير محدد',
          state: location.state || 'غير محدد',
          stateArabic: location.stateArabic || 'غير محدد',
          timezone: location.timezone || 'Unknown'
        };
      } catch (e) {
        console.log('Error parsing stored location:', e);
      }
    } else {
      // Fetch location asynchronously (don't wait for it, but update for next visit)
      getLocationFromIP().then(loc => {
        if (loc && loc.state) {
          localStorage.setItem('visitorLocation', JSON.stringify(loc));
        }
      }).catch(err => {
        console.log('Location fetch error:', err);
      });
    }

    const visitData = {
      visitorId,
      sessionId,
      pagePath,
      pageTitle: pageTitle || pagePath,
      timestamp: serverTimestamp(),
      date: new Date().toISOString().split('T')[0], // YYYY-MM-DD format
      hour: new Date().getHours(),
      dayOfWeek: new Date().getDay(), // 0 = Sunday, 6 = Saturday
      userAgent: navigator.userAgent,
      referrer: document.referrer || 'direct',
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      deviceType,
      browser,
      os,
      country: location.country,
      countryCode: location.countryCode,
      city: location.city,
      state: location.state,
      stateArabic: location.stateArabic,
      timezone: location.timezone,
      isNewVisitor: !localStorage.getItem('hasVisitedBefore')
    };

    // Mark as returning visitor after first visit
    if (!localStorage.getItem('hasVisitedBefore')) {
      localStorage.setItem('hasVisitedBefore', 'true');
    }

    await addDoc(collection(db, 'visits'), visitData);
  } catch (error) {
    console.error('Error tracking page visit:', error);
  }
};

// Track session end (when user leaves)
export const trackSessionEnd = async () => {
  try {
    const sessionId = sessionStorage.getItem('sessionId');
    const sessionStart = sessionStorage.getItem('sessionStart');
    
    if (sessionId && sessionStart) {
      const sessionDuration = Date.now() - parseInt(sessionStart);
      
      const sessionData = {
        sessionId,
        visitorId: getVisitorId(),
        duration: sessionDuration, // in milliseconds
        timestamp: serverTimestamp(),
        date: new Date().toISOString().split('T')[0],
        endedAt: new Date().toISOString()
      };

      await addDoc(collection(db, 'sessions'), sessionData);
      
      // Clear session storage
      sessionStorage.removeItem('sessionId');
      sessionStorage.removeItem('sessionStart');
    }
  } catch (error) {
    console.error('Error tracking session end:', error);
  }
};

// Get visitor statistics
export const getVisitorStats = async () => {
  try {
    // Get all visits
    const visitsSnapshot = await getDocs(collection(db, 'visits'));
    const allVisits = visitsSnapshot.docs.map(doc => {
      const data = doc.data();
      return { 
        id: doc.id, 
        ...data,
        // Ensure timestamp is handled properly
        timestamp: data.timestamp || null,
        date: data.date || (data.timestamp?.toDate ? data.timestamp.toDate().toISOString().split('T')[0] : new Date().toISOString().split('T')[0]),
        hour: data.hour !== undefined ? data.hour : (data.timestamp?.toDate ? data.timestamp.toDate().getHours() : new Date().getHours()),
        dayOfWeek: data.dayOfWeek !== undefined ? data.dayOfWeek : (data.timestamp?.toDate ? data.timestamp.toDate().getDay() : new Date().getDay()),
        // Ensure device/browser/OS fields exist
        deviceType: data.deviceType || 'desktop',
        browser: data.browser || 'Other',
        os: data.os || 'Other',
        // Ensure state fields exist
        state: data.state || data.stateArabic || 'غير محدد',
        stateArabic: data.stateArabic || data.state || 'غير محدد'
      };
    });
    
    console.log(`📊 Total visits in database: ${allVisits.length}`);
    
    // Filter out admin page visits
    const visits = allVisits.filter(v => !isAdminPage(v.pagePath || '/'));
    
    console.log(`📊 Visits after filtering admin pages: ${visits.length}`);

    // Get all sessions
    const sessionsSnapshot = await getDocs(collection(db, 'sessions'));
    const sessions = sessionsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    // Calculate unique visitors (only from non-admin visits)
    const uniqueVisitors = new Set(visits.map(v => v.visitorId));
    const newVisitors = visits.filter(v => v.isNewVisitor).length;
    const returningVisitors = uniqueVisitors.size - newVisitors;

    // Group visits by date
    const visitsByDate = {};
    visits.forEach(visit => {
      const date = visit.date || (visit.timestamp?.toDate ? visit.timestamp.toDate().toISOString().split('T')[0] : new Date().toISOString().split('T')[0]);
      visitsByDate[date] = (visitsByDate[date] || 0) + 1;
    });

    // Group visits by hour
    const visitsByHour = {};
    visits.forEach(visit => {
      const hour = visit.hour !== undefined ? visit.hour : (visit.timestamp?.toDate ? visit.timestamp.toDate().getHours() : new Date().getHours());
      visitsByHour[hour] = (visitsByHour[hour] || 0) + 1;
    });

    // Group visits by day of week
    const visitsByDay = {};
    const dayNames = ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
    visits.forEach(visit => {
      const day = visit.dayOfWeek !== undefined ? visit.dayOfWeek : (visit.timestamp?.toDate ? visit.timestamp.toDate().getDay() : new Date().getDay());
      const dayName = dayNames[day];
      visitsByDay[dayName] = (visitsByDay[dayName] || 0) + 1;
    });

    // Calculate average session duration
    const validSessions = sessions.filter(s => s.duration && s.duration > 0);
    const avgSessionDuration = validSessions.length > 0
      ? validSessions.reduce((sum, s) => sum + s.duration, 0) / validSessions.length
      : 0;

    // Get most visited pages (exclude admin pages)
    const pageViews = {};
    visits.forEach(visit => {
      const page = visit.pagePath || '/';
      // Filter out admin pages
      if (!isAdminPage(page)) {
        pageViews[page] = (pageViews[page] || 0) + 1;
      }
    });

    const topPages = Object.entries(pageViews)
      .map(([path, count]) => ({ path, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    // Get last 30 days data for chart
    const last30Days = [];
    for (let i = 29; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      last30Days.push({
        date: dateStr,
        visits: visitsByDate[dateStr] || 0
      });
    }

    // Get last 7 days for weekly chart
    const last7Days = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      const dayName = dayNames[date.getDay()];
      last7Days.push({
        date: dateStr,
        dayName,
        visits: visitsByDate[dateStr] || 0
      });
    }

    // Group by device type
    const visitsByDevice = {};
    visits.forEach(visit => {
      const device = visit.deviceType || 'desktop';
      visitsByDevice[device] = (visitsByDevice[device] || 0) + 1;
    });
    
    console.log('Visits by device:', visitsByDevice);

    // Group by Algerian state (wilaya)
    const visitsByState = {};
    visits.forEach(visit => {
      // Use Arabic state name if available, otherwise use English, otherwise default
      const state = visit.stateArabic || visit.state || 'غير محدد';
      visitsByState[state] = (visitsByState[state] || 0) + 1;
    });
    
    console.log('Visits by state:', visitsByState);

    // Group by browser
    const visitsByBrowser = {};
    visits.forEach(visit => {
      const browser = visit.browser || 'Other';
      visitsByBrowser[browser] = (visitsByBrowser[browser] || 0) + 1;
    });

    // Group by OS
    const visitsByOS = {};
    visits.forEach(visit => {
      const os = visit.os || 'Other';
      visitsByOS[os] = (visitsByOS[os] || 0) + 1;
    });

    // Get top Algerian states (wilayas)
    const topStates = Object.entries(visitsByState)
      .map(([state, count]) => ({ state, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 20); // Show top 20 wilayas

    return {
      totalVisits: visits.length,
      uniqueVisitors: uniqueVisitors.size,
      newVisitors,
      returningVisitors,
      totalSessions: sessions.length,
      avgSessionDuration: Math.round(avgSessionDuration / 1000), // Convert to seconds
      visitsByDate,
      visitsByHour,
      visitsByDay,
      topPages,
      last30Days,
      last7Days,
      visitsByDevice,
      visitsByState,
      visitsByBrowser,
      visitsByOS,
      topStates
    };
  } catch (error) {
    console.error('Error getting visitor stats:', error);
    return {
      totalVisits: 0,
      uniqueVisitors: 0,
      newVisitors: 0,
      returningVisitors: 0,
      totalSessions: 0,
      avgSessionDuration: 0,
      visitsByDate: {},
      visitsByHour: {},
      visitsByDay: {},
      topPages: [],
      last30Days: [],
      last7Days: [],
      visitsByDevice: {},
      visitsByState: {},
      visitsByBrowser: {},
      visitsByOS: {},
      topStates: []
    };
  }
};

// Initialize tracking on page load
export const initializeTracking = () => {
  // Track current page visit
  const pagePath = window.location.pathname;
  const pageTitle = document.title;
  trackPageVisit(pagePath, pageTitle);

  // Track session end when user leaves
  window.addEventListener('beforeunload', trackSessionEnd);
  window.addEventListener('pagehide', trackSessionEnd);
};

// Track page change (to be called from React Router)
export const trackPageChange = (pathname, title) => {
  trackPageVisit(pathname, title || document.title);
};

